import { useEffect, useState } from "react";
import { Button } from "./ui";
import { legalUrls } from "../lib/legal";
import {
  clearAnalyticsCookies,
  measurementId,
  readConsent,
  startAnalytics,
  trackPageView,
  analyticsPath,
  writeConsent,
  type Consent,
} from "../lib/analytics";

const OPEN_EVENT = "sm:cookie-settings";

/** Re-opens the banner (the footer's "Cookie settings"). */
export function openCookieSettings(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

function start(pathname: string) {
  if (!measurementId) return;
  startAnalytics(measurementId, {
    sendPageView: false,
    pageLocation: window.location.origin + analyticsPath(pathname),
  });
  trackPageView(pathname);
}

/**
 * Asks once whether Google Analytics may run; the answer is shared with the
 * landing site (lib/analytics.ts). Also sends a page view per navigation once
 * analytics is on. Renders nothing without a measurement ID.
 */
export function ConsentBanner({ pathname }: { pathname: string }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!measurementId) return;
    const consent = readConsent();
    if (consent === "granted") start(window.location.pathname);
    if (consent === "denied") clearAnalyticsCookies();
    if (consent === null) setOpen(true);
    const reopen = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, reopen);
    return () => window.removeEventListener(OPEN_EVENT, reopen);
  }, []);

  useEffect(() => trackPageView(pathname), [pathname]);

  if (!measurementId || !open) return null;

  function choose(choice: Consent) {
    const wasGranted = readConsent() === "granted";
    writeConsent(choice);
    setOpen(false);
    if (choice === "granted") start(window.location.pathname);
    else if (wasGranted) {
      // gtag.js is already running; a reload is the clean way to stop it.
      clearAnalyticsCookies();
      window.location.reload();
    }
  }

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed inset-x-4 bottom-4 z-50 sm:right-auto sm:max-w-md"
    >
      <div className="tactile edge-line-strong rounded-2xl p-4">
        <p className="text-sm font-semibold text-ink">
          Can we use analytics cookies? Google Analytics shows us how people
          find and use SVG Mascot. Nothing is set unless you allow it.{" "}
          <a
            href={`${legalUrls.privacy}#cookies`}
            target="_blank"
            rel="noreferrer"
            className="text-lilac-600 underline"
          >
            Privacy Policy
          </a>
        </p>
        <div className="mt-3 flex gap-2">
          <Button size="sm" variant="outline" onClick={() => choose("granted")}>
            Allow analytics
          </Button>
          <Button size="sm" variant="outline" onClick={() => choose("denied")}>
            Reject
          </Button>
        </div>
      </div>
    </div>
  );
}
